import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as Copy } from "../../assets/svg/copy.svg";
import { Button, ButtonThemeEnum } from "../../components/Buttons/styles";
import { Input } from "../../components/shared/Input";
import { LINKS } from "../../constants/links";
import { useReferralCodes } from "../../hooks/api/referrals/useReferralCodes";
import { useToast } from "../../hooks/toast";
import { copyStringToClipboard } from "../../utils/strings";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import {
  CodeActions,
  CodeLinkText,
  CodeList,
  CodeMeta,
  CodeRow,
  CodesHeader,
  CodesSection,
  CreateRow,
  EmptyCodes,
  ErrorText,
} from "./codesStyle";

interface IReferralCodesProps {
  isMobileScreen?: boolean;
}

export function ReferralCodes({ isMobileScreen }: IReferralCodesProps) {
  const { codes, isValidating, createCode, deleteCode } = useReferralCodes();
  const { addToast } = useToast();
  const { t } = useTranslation("app", {
    keyPrefix: "pages.ReferralsTab.ReferralsTab",
  });

  const [customCode, setCustomCode] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const link = (code: string) => `${LINKS.app}/r/${code}`;

  const onCopy = useCallback(
    (code: string) => {
      copyStringToClipboard(link(code));
      addToast({
        type: ToastEnum.SIMPLE,
        header: t("referral_link_copied"),
        status: ToastStatusEnum.SUCCESS,
      });
    },
    [addToast, t]
  );

  const onCreate = useCallback(async () => {
    setError(null);
    setSubmitting(true);
    try {
      await createCode(customCode.trim() || undefined, label.trim() || undefined);
      setCustomCode("");
      setLabel("");
      addToast({
        type: ToastEnum.SIMPLE,
        header: t("code_created"),
        status: ToastStatusEnum.SUCCESS,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }, [createCode, customCode, label, addToast, t]);

  const onDelete = useCallback(
    async (code: string) => {
      setError(null);
      try {
        await deleteCode(code);
        addToast({
          type: ToastEnum.SIMPLE,
          header: t("code_deleted"),
          status: ToastStatusEnum.SUCCESS,
        });
      } catch (e) {
        setError((e as Error).message);
      }
    },
    [deleteCode, addToast, t]
  );

  return (
    <CodesSection>
      <CodesHeader>
        <h3>{t("your_referral_codes")}</h3>
        <span>{codes.length}/20</span>
      </CodesHeader>

      <CreateRow $isMobileScreen={isMobileScreen}>
        <Input
          type="text"
          placeholder={t("custom_code_placeholder")}
          value={customCode}
          onChange={(e) => setCustomCode(e.target.value.toLowerCase())}
          maxLength={32}
        />
        <Input
          type="text"
          placeholder={t("code_label_placeholder")}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={64}
        />
        <Button
          buttonTheme={ButtonThemeEnum.PRIMARY}
          onClick={onCreate}
          disabled={submitting || codes.length >= 20}
        >
          {customCode.trim() ? t("create_code") : t("generate_code")}
        </Button>
      </CreateRow>

      {error && <ErrorText>{error}</ErrorText>}

      {codes.length === 0 ? (
        <EmptyCodes>{isValidating ? t("loading") : t("no_codes_yet")}</EmptyCodes>
      ) : (
        <CodeList>
          {codes.map((c) => (
            <CodeRow key={c.code}>
              <CodeMeta>
                <CodeLinkText>
                  {window.location.origin}/r/<span>{c.code}</span>
                </CodeLinkText>
                {c.label && <div className="label">{c.label}</div>}
              </CodeMeta>
              <CodeActions>
                <button
                  type="button"
                  onClick={() => onCopy(c.code)}
                  title={t("copy_link")}
                >
                  <Copy />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(c.code)}
                  title={t("delete_code")}
                >
                  ✕
                </button>
              </CodeActions>
            </CodeRow>
          ))}
        </CodeList>
      )}
    </CodesSection>
  );
}
